import { useCallback } from 'react'
import { simpleEditNotification } from 'hooks/notificationHandler'
import {
  UpsertConcreteNotificationInput,
  useUpsertConcreteNotificationMutation,
} from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { useRecipientsSelectContext } from 'components/CIMSSelect/common/RecipientsSelect/RecipientsSelectContext'
import { useConcreteNotificationsContext } from '../../ConcreteNotificationsContext'
import { EditNotificationEventOptionsFn } from 'components/CIMSNotifications/types'
import { Permission } from '@cims-online/shared'
import { LabeledValueType } from 'components/CIMSSelect/types'
import { ConcreteMaterialFormValue, ConcreteMixDesignFormValue } from './types'
import RenderFormItems from './components/RenderFormItems'
import { useProjectContext } from 'context/ProjectContext'

export function useEditFn() {
  const { projectId } = useProjectContext()
  const { recipients, refetch } = useConcreteNotificationsContext()
  const { toLabeledValues, fromLabeledValues } = useRecipientsSelectContext()
  const [edit] = useUpsertConcreteNotificationMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      upsertConcreteNotification: {
        notification: { notificationEvent },
      },
    }) => simpleEditNotification(notificationEvent),
  })
  return useCallback<EditNotificationEventOptionsFn<InitialValues>>(
    (event) => {
      const { every, byConcreteMaterial, byConcreteMixDesign } =
        recipients(event)
      return {
        render: <RenderFormItems />,
        initialValues: {
          recipients: every ? toLabeledValues(every) : [],
          concreteMaterial: byConcreteMaterial.map(
            ({ concreteMaterial, recipients }) => ({
              concreteMaterialId: concreteMaterial.globalId,
              recipients: toLabeledValues(recipients),
            })
          ),
          concreteMixDesign: byConcreteMixDesign.map(
            ({ concreteMixDesign, recipients }) => ({
              concreteMixDesignId: concreteMixDesign.globalId,
              recipients: toLabeledValues(recipients),
            })
          ),
        },
        permission: Permission.EditConcreteNotification,
        onSave: async ({ recipients, concreteMaterial, concreteMixDesign }) => {
          const input: UpsertConcreteNotificationInput = {
            notificationEventId: event.id,
            projectId,
            every: fromLabeledValues(recipients),
            byConcreteMaterial: concreteMaterial?.map(
              ({ recipients, ...rest }) => ({
                ...rest,
                recipients: fromLabeledValues(recipients),
              })
            ),
            byConcreteMixDesign: concreteMixDesign?.map(
              ({ recipients, ...rest }) => ({
                ...rest,
                recipients: fromLabeledValues(recipients),
              })
            ),
          }

          await edit({ variables: { input } })
          refetch()
        },
        onClear: async () => {
          await edit({
            variables: {
              input: {
                notificationEventId: event.id,
                projectId,
                every: { userIds: [], nonUserEmailIds: [] },
                byConcreteMaterial: [],
                byConcreteMixDesign: [],
              },
            },
          })
        },
      }
    },
    [edit, recipients, projectId, refetch, toLabeledValues, fromLabeledValues]
  )
}

interface InitialValues {
  recipients: LabeledValueType[]
  concreteMaterial?: ConcreteMaterialFormValue[]
  concreteMixDesign?: ConcreteMixDesignFormValue[]
}
